// ABOUTME: SPEC-82 §4.2 — the bottom-band half of useVoiceFirstChrome: the footers' `voiceFirst` object (the dock, the pre-tap CTA,
// ABOUTME: the revealed controls). Split out (review M3) so each unit stays under the function-size gate; behaviour is unchanged.

import { useEffect, useMemo } from 'react';
import { useDeviceProfile } from '../components/Projectionist/useViewportSize';
import useControlReveal from '../components/VoiceFirst/VoiceDock/useControlReveal';
import { setBottomChrome } from './bottomChromeStore';
import { talkTo, connectingTo } from './copy';

/**
 * @param {object} p
 * @param {Readonly<Record<string, true>>} p.ux the readUiExperiment() map
 * @param {object} p.introX useIntroExperiment()'s return (holding / ctaVisible / tap)
 * @param {string} p.brandName from /api/config — never a literal here
 * @param {boolean} p.footerShown the footers are mounted (Enter pressed, film over); gates publishing the open row's lift
 * @param {boolean} [p.connecting] the agent is starting (tapped, no words yet): the dots carry a 'Connecting to …' bubble
 * @returns {{ footer: (object|null), hideFlanks: boolean }} `footer` is null when no bottom toggle applies
 */
export default function useVoiceDockFooter({ ux, introX, brandName, footerShown, connecting = false }) {
  const { reducedMotion } = useDeviceProfile();
  // The dock exists when any of the three toggles that need it applies; the pre-tap CTA needs it too.
  const dock = !!(ux.glow_ring || ux.control_reveal || introX.holding);
  // D-17: pre-tap only the CTA exists; with control_reveal the flanks live inside the row instead.
  const hideFlanks = !!(ux.control_reveal || introX.holding);
  const revealEnabled = !!ux.control_reveal && !introX.holding;
  // Open by default the moment the dots are on screen (owner change): the lift is published only while the footer is actually mounted
  // (after Enter and the film), so a row that is "open" behind the Enter gate or a playing film never lifts the band.
  const reveal = useControlReveal({ enabled: revealEnabled && footerShown, reducedMotion });

  // Bottom-band consumers that hang off the flanks (the spec-sheet pill) read this; cleared when the
  // experiment goes away so nothing keeps re-anchoring to a flank that is back.
  useEffect(() => {
    setBottomChrome({ flanksHidden: hideFlanks });
    return () => setBottomChrome({ flanksHidden: false });
  }, [hideFlanks]);

  // `side`: talk_label_right puts the phone's label beside the mic instead of over it (VoiceDock; desktop ignores it).
  const talk = useMemo(
    () => (introX.ctaVisible ? { label: talkTo(brandName), onTap: introX.tap, side: !!ux.talk_label_right } : null),
    [introX.ctaVisible, introX.tap, brandName, ux.talk_label_right],
  );
  const revealProps = useMemo(
    () => ({ enabled: revealEnabled, open: reveal.open, onToggle: reveal.toggle }),
    [revealEnabled, reveal.open, reveal.toggle],
  );
  // `connecting`: the bubble over the dots between the tap and the agent's first words (null otherwise, and always flag-off).
  const connectingLabel = connecting && !talk ? connectingTo(brandName) : null;
  const footer = useMemo(() => {
    if (!dock && !ux.bare_footer) return null;
    return {
      dock, hideFlanks, bare: !!ux.bare_footer, ring: !!ux.glow_ring, talk, reveal: revealProps, reducedMotion,
      ...(connectingLabel ? { connecting: connectingLabel } : {}),
    };
  }, [dock, hideFlanks, ux.bare_footer, ux.glow_ring, talk, revealProps, reducedMotion, connectingLabel]);

  return { footer, hideFlanks };
}
